
import React, { useState, useRef, useEffect } from 'react';
import { getMagicalGuideResponse } from '../services/geminiService';

interface SpiritGuideProps {
  roomName: string;
  onClose: () => void;
}

const SpiritGuide: React.FC<SpiritGuideProps> = ({ roomName, onClose }) => {
  const [messages, setMessages] = useState<{role: 'guide' | 'user', text: string}[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const greetings: Record<string, string> = {
      "Eliksirlaboratoriet": "Jeg mærker din nysgerrighed blande sig med duften af stjernestøv... Hvilken bryg søger du svar på?",
      "Drivhuset": "Planterne hvisker dit navn, unge eventyrer. Kan du mærke, hvordan rødderne under os lytter?",
      "Runekammeret": "Stenene her husker alt. Hvilken gammel hemmelighed ønsker du at afdække?",
      "Storsalen": "Velkommen til Norewyns hjerte. Her er energien altid lys. Hvad bringer dig til vores bord?",
    };
    
    const initialText = greetings[roomName] || `Vær hilset, eventyrer. Jeg mærker din tilstedeværelse her i ${roomName}. Hvad ønsker du at vide?`;
    setMessages([{ role: 'guide', text: initialText }]);
  }, [roomName]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isTyping) return;

    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setIsTyping(true);

    const response = await getMagicalGuideResponse(userMsg, roomName);
    setMessages(prev => [...prev, { role: 'guide', text: response }]);
    setIsTyping(false);
  };

  return (
    <div className="fixed bottom-24 right-6 md:right-10 z-[350] w-[calc(100%-3rem)] md:w-96 h-[500px] bg-[#050510]/95 border-2 border-cyan-400/40 rounded-[2.5rem] shadow-[0_0_60px_rgba(34,211,238,0.25)] backdrop-blur-2xl flex flex-col overflow-hidden animate-fade-in ring-1 ring-white/10">
      {/* Nebula baggrunds-animation */}
      <div className="absolute inset-0 pointer-events-none opacity-20">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,#22d3ee22,transparent_70%)] animate-pulse" />
      </div>
      
      <div className="p-6 bg-cyan-950/40 border-b border-cyan-400/20 flex justify-between items-center relative z-10">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="w-4 h-4 bg-cyan-400 rounded-full animate-pulse shadow-[0_0_15px_rgba(34,211,238,1)]" />
            <div className="absolute inset-0 w-4 h-4 bg-cyan-400 rounded-full animate-ping opacity-20" />
          </div>
          <div>
            <span className="font-cinzel text-cyan-400 text-[11px] tracking-[0.3em] uppercase font-bold block">Spirit Guide</span>
            <span className="text-[7px] text-cyan-200/50 uppercase tracking-widest font-black">Det gamle Ekko</span>
          </div>
        </div>
        <button onClick={onClose} className="text-cyan-400/40 hover:text-cyan-400 text-3xl transition-colors">&times;</button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar relative z-10">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'guide' ? 'justify-start' : 'justify-end'} animate-fade-in`}>
            <div className={`max-w-[85%] p-4 rounded-2xl text-[13px] leading-relaxed shadow-xl transition-all ${
              m.role === 'guide' 
                ? 'bg-cyan-900/30 text-cyan-50 rounded-tl-none border border-cyan-400/20 italic font-medium shadow-[0_0_15px_rgba(34,211,238,0.05)]' 
                : 'bg-indigo-600/80 text-white rounded-tr-none border border-indigo-400/30'
            }`}>
              {m.text}
            </div>
          </div>
        ))}
        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-cyan-900/20 p-4 rounded-full flex gap-1.5 items-center border border-cyan-400/10">
              <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
              <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
              <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={handleSend} className="p-5 bg-black/40 border-t border-cyan-400/10 flex gap-3 relative z-10">
        <input 
          type="text" 
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Hvisk dine ord..." 
          className="flex-1 bg-white/5 border border-cyan-400/20 rounded-2xl px-5 py-3 text-white text-xs focus:outline-none focus:border-cyan-400/50 transition-all placeholder:text-cyan-400/20"
        />
        <button type="submit" disabled={isTyping} className="bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white p-3 rounded-2xl transition-all shadow-lg active:scale-95">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>
        </button>
      </form>
    </div>
  );
};

export default SpiritGuide;
